import { useCallback, useEffect, useMemo, useState } from 'react';
import { presetFor } from '@app/shared';
import { ApiFailure, isApiFailure } from '../../api/http';
import type { SlotDto } from '../../api/types';
import { useAppApi } from '../../app/ApiProvider';
import { dayKeyInTz, dayKeyLabel, dayKeyRange, formatAmount } from '../../domain/datetime';
import { useAsync } from '../../hooks/useAsync';
import { capitalize } from '../../i18n/dict';
import { useI18n } from '../../i18n/I18nProvider';
import { BottomSheet } from '../../ui/BottomSheet';
import { CtaBar, CtaButton } from '../../ui/Cta';
import { Avatar } from '../../ui/controls';
import { EmptyState, ErrorState, Loader } from '../../ui/feedback';
import { Screen, ScrollArea, Section, SectionTitle, TopBar } from '../../ui/Screen';
import { RestrictionSheet, restrictionFromFailure, type Restriction } from './RestrictionSheet';

/** Глубина полосы дней. Две недели — столько же, сколько показывает прототип. */
const VISIBLE_DAYS = 14;

/**
 * Запись: услуга → специалист → день → слот → подтверждение (ТЗ §14.1).
 *
 * Слоты запрашиваются у сервера на каждую тройку (услуга, специалист, день) и НЕ считаются
 * здесь: сетку строит `slotService` с учётом рабочих часов, отпусков, длительности услуги
 * и уже занятых интервалов (ТЗ §7). Вторая реализация на фронте разошлась бы с первой
 * и предлагала бы время, которое сервер отвергнет.
 *
 * Метка времени (`slot.label`) приходит готовой и не пересчитывается: она посчитана
 * в поясе организации тем же кодом, что считает напоминания (AGENTS.md, правило 1).
 */
export function BookingScreen(props: {
  slug: string;
  initialServiceId?: string | undefined;
  onBack: () => void;
  onBooked: () => void;
}): JSX.Element {
  const { t, lang } = useI18n();
  const api = useAppApi();

  const page = useAsync(
    useCallback(() => api.client.getBusiness(props.slug), [api, props.slug]),
    [props.slug],
  );

  const [serviceId, setServiceId] = useState<string | null>(props.initialServiceId ?? null);
  const [staffId, setStaffId] = useState<string | null>(null);
  const [day, setDay] = useState<string | null>(null);
  const [slot, setSlot] = useState<SlotDto | null>(null);

  const [submitting, setSubmitting] = useState(false);
  const [booked, setBooked] = useState(false);
  const [failure, setFailure] = useState<unknown>(null);
  const [restriction, setRestriction] = useState<Restriction | null>(null);

  const data = page.state.status === 'ready' ? page.state.data : null;

  // Подстановка первых значений после загрузки. В эффекте, а не в useState: списки услуг
  // и специалистов приходят с сервера, и на первом рендере их ещё нет.
  useEffect(() => {
    if (!data) return;
    setServiceId((current) =>
      current && data.services.some((service) => service.id === current)
        ? current
        : (data.services[0]?.id ?? null),
    );
    setStaffId((current) =>
      current && data.staff.some((member) => member.id === current)
        ? current
        : (data.staff[0]?.id ?? null),
    );
    setDay((current) => current ?? dayKeyInTz(new Date(), data.business.tz));
  }, [data]);

  const days = useMemo(
    () => (data ? dayKeyRange(dayKeyInTz(new Date(), data.business.tz), VISIBLE_DAYS) : []),
    [data],
  );

  const slotsReady = Boolean(serviceId && staffId && day);

  const slots = useAsync(
    useCallback(async () => {
      if (!serviceId || !staffId || !day) return null;
      return api.client.getSlots({ slug: props.slug, serviceId, staffId, date: day });
    }, [api, props.slug, serviceId, staffId, day]),
    [props.slug, serviceId, staffId, day],
  );

  // Выбранный слот перестаёт существовать при смене услуги, специалиста или дня.
  // Не сбросив его, кнопка отправила бы на сервер время из чужой сетки.
  useEffect(() => setSlot(null), [serviceId, staffId, day]);

  if (page.state.status === 'loading') {
    return (
      <Screen>
        <TopBar title={t('bookTitle')} onBack={props.onBack} />
        <Loader />
      </Screen>
    );
  }

  if (page.state.status === 'failed' || !data) {
    return (
      <Screen>
        <TopBar title={t('bookTitle')} onBack={props.onBack} />
        <ErrorState
          // Ветка `!data` при успешном запросе — рассогласование внутри приложения, а не
          // отказ сервера. Сырой `new Error('нет данных')` показал бы русский текст мимо
          // словаря: `ErrorState` переводит только ключи, а обычной ошибке печатает
          // `message` как есть — на узбекском интерфейсе это была бы русская строка.
          error={
            page.state.status === 'failed'
              ? page.state.error
              : new ApiFailure('err.internal', 500)
          }
          onRetry={page.reload}
        />
      </Screen>
    );
  }

  const { business, services, staff } = data;
  const preset = presetFor(business.type);

  const submit = async (): Promise<void> => {
    if (!slot || !serviceId || !staffId || submitting) return;
    setSubmitting(true);
    setFailure(null);
    try {
      await api.client.createAppointment({
        businessSlug: props.slug,
        serviceId,
        staffId,
        startsAt: slot.startsAt,
      });
      setBooked(true);
    } catch (error) {
      // Бан — не «ошибка формы»: у него своя шторка с датой снятия и объяснением
      // причины (ТЗ §14.2). Остальные отказы показываются текстом по ключу i18n.
      const asRestriction = isApiFailure(error)
        ? restrictionFromFailure(error.key, error.details, business.name)
        : null;
      if (asRestriction) setRestriction(asRestriction);
      else setFailure(error);
      // Слот мог уйти другому клиенту, пока человек думал: сетку надо перечитать.
      slots.reload();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Screen>
      <TopBar title={t('bookTitle')} subtitle={business.name} onBack={props.onBack} />

      <ScrollArea bottomGap="cta">
        <Section>
          <SectionTitle>{t('stepService')}</SectionTitle>
          {services.length === 0 ? (
            <EmptyState>{t('emptyServices')}</EmptyState>
          ) : (
            services.map((service) => (
              <button
                key={service.id}
                type="button"
                onClick={() => setServiceId(service.id)}
                className={`mb-2 flex w-full items-center justify-between gap-2.5 rounded-[13px] px-3.5 py-3.5 text-left transition ${
                  service.id === serviceId
                    ? 'bg-[color-mix(in_srgb,var(--accent)_10%,var(--surface-2))] shadow-[inset_0_0_0_1.5px_var(--accent)]'
                    : 'bg-surface-2'
                }`}
              >
                <div className="min-w-0">
                  <div className="text-[14.5px] font-semibold">
                    {lang === 'ru' ? service.titleRu : service.titleUz}
                  </div>
                  <div className="mt-0.5 text-[12px] text-ink-3">
                    {service.durationMin} {t('min')}
                  </div>
                </div>
                <div className="whitespace-nowrap text-[14px] font-bold text-accent">
                  {formatAmount(service.priceSum, lang)} {t('sum')}
                </div>
              </button>
            ))
          )}
        </Section>

        <Section>
          {/* Шаг 2 говорит на языке отрасли: «2 · Врач» у стоматологии, «2 · Преподаватель»
              у репетитора. Обобщённое «Специалист» обесценило бы весь смысл пресетов. */}
          <SectionTitle>
            {t('stepSpecialistPrefix')}
            {capitalize(t(preset.specialistTerm))}
          </SectionTitle>
          {staff.length === 0 ? (
            <EmptyState>{t('emptyStaff')}</EmptyState>
          ) : (
            <div className="no-scrollbar flex gap-2 overflow-x-auto py-0.5">
              {staff.map((member) => (
                <button
                  key={member.id}
                  type="button"
                  onClick={() => setStaffId(member.id)}
                  className={`flex w-[84px] shrink-0 flex-col items-center gap-1.5 rounded-[14px] px-2 py-2.5 transition ${
                    member.id === staffId
                      ? 'bg-surface shadow-[inset_0_0_0_2px_var(--accent)]'
                      : 'bg-surface-2'
                  }`}
                >
                  <Avatar name={member.name} size="sm" />
                  <span className="clamp-2 text-center text-[12px] font-semibold leading-[1.25]">
                    {member.name}
                  </span>
                  {member.title ? (
                    <span className="clamp-2 text-center text-[10.5px] leading-[1.2] text-ink-3">
                      {member.title}
                    </span>
                  ) : null}
                </button>
              ))}
            </div>
          )}
        </Section>

        <Section>
          <SectionTitle>{t('stepDay')}</SectionTitle>
          <div className="no-scrollbar flex gap-2 overflow-x-auto py-0.5">
            {days.map((key) => {
              const label = dayKeyLabel(key, lang);
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => setDay(key)}
                  className={`flex min-w-[52px] shrink-0 flex-col items-center gap-0.5 rounded-[12px] px-3.5 py-2.5 transition ${
                    key === day ? 'bg-accent text-on-accent' : 'bg-surface-2 text-ink-2'
                  }`}
                >
                  <small className="text-[10px] font-semibold opacity-80">{label.weekday}</small>
                  <b className="text-[15px] font-extrabold">{label.day}</b>
                </button>
              );
            })}
          </div>
        </Section>

        <Section className="pb-2">
          <SectionTitle>{t('stepTime')}</SectionTitle>
          {!slotsReady ? null : slots.state.status === 'loading' ? (
            <Loader />
          ) : slots.state.status === 'failed' ? (
            <ErrorState error={slots.state.error} onRetry={slots.reload} />
          ) : slots.state.data && slots.state.data.slots.length > 0 ? (
            <div className="grid grid-cols-4 gap-2">
              {slots.state.data.slots.map((item) => (
                <button
                  key={item.startsAt}
                  type="button"
                  onClick={() => setSlot(item)}
                  className={`rounded-[11px] py-2.5 text-center text-[13.5px] font-bold transition ${
                    item.startsAt === slot?.startsAt
                      ? 'bg-accent text-on-accent'
                      : 'bg-surface-2 text-ink'
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </div>
          ) : (
            <EmptyState>{t('slotsEmpty')}</EmptyState>
          )}
        </Section>
      </ScrollArea>

      <CtaBar>
        <CtaButton onClick={() => void submit()} disabled={!slot || submitting}>
          {slot ? `${t('ctaBook')} · ${slot.label}` : t('pickTime')}
        </CtaButton>
      </CtaBar>

      <BottomSheet open={booked} title={t('bookedT')} onClose={props.onBooked}>
        <div className="mb-4 text-[13px] leading-[1.5] text-ink-2">{t('bookedB')}</div>
        <CtaButton onClick={props.onBooked}>{t('goToMyBookings')}</CtaButton>
      </BottomSheet>

      <BottomSheet
        open={failure !== null}
        title={t('errTitle')}
        onClose={() => setFailure(null)}
      >
        <ErrorState error={failure} withTitle={false} />
        <CtaButton variant="ghost" onClick={() => setFailure(null)}>
          {t('close')}
        </CtaButton>
      </BottomSheet>

      <RestrictionSheet
        open={restriction !== null}
        restriction={restriction}
        onClose={() => setRestriction(null)}
      />
    </Screen>
  );
}
